/* Session records (sessions[date]), the readiness check-in (recovery[date])
 * and cycle changes that never relabel the past. Pure: state in, new state
 * out (the same object when nothing changes, so no save is triggered). The
 * shapes are in docs/fitness-data-model.md. */
import { dateKey, defaultCycleAnchor, parseAnchor } from './state';
import { isTrainingDate, planDayFor } from './logic/schedule';
import { buildRecords } from './logic/records';
import { deloadOn } from './logic/deload';
import { sumDayVolume } from './workout';

const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const own = (o, k) => (isObj(o) && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined);

export const MAX_SESSION_MIN = 300; // longer "sessions" are sets ticked after the fact
export const SLEEP_CHOICES = [5, 6, 7, 8, 9]; // 5 = "<=5", 9 = "9+"
export const SORE_AREAS = ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Forearms', 'Quads', 'Hamstrings',
  'Glutes', 'Calves', 'Core'];

/* Which plan day a date is: its sessions stamp first, then the cycle. */
export function dayFor(state, date) {
  return planDayFor(state, date, defaultCycleAnchor());
}

function withSession(state, date, next) {
  const sessions = { ...(isObj(state.sessions) ? state.sessions : {}) };
  if (Object.keys(next).length) sessions[date] = next;
  else delete sessions[date];
  return { ...state, sessions };
}

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* A ticked set stamps the plan day the date was trained as (once), and a live
 * tick also times the session (startedAt once, lastSetAt every tick). */
export function stampSession(state, date, { day, now = null } = {}) {
  const prev = isObj(own(state.sessions, date)) ? state.sessions[date] : {};
  const next = { ...prev };
  if (!next.planDayId && !next.planDayName && isObj(day) && day.id) {
    next.planDayId = day.id;
    next.planDayName = day.name;
    if (!next.source) next.source = 'app';
  }
  if (typeof now === 'number') {
    if (typeof next.startedAt !== 'number') next.startedAt = now;
    next.lastSetAt = now;
  }
  return same(prev, next) ? state : withSession(state, date, next);
}

/* "Train anyway" / "Do another day": train `day` on `date`. Choosing the day
 * the cycle already gives drops the choice again. */
export function chooseDay(state, date, day) {
  const prev = isObj(own(state.sessions, date)) ? state.sessions[date] : {};
  const { planDayId, planDayName, override, source, ...rest } = prev;
  const cycleDay = planDayFor({ ...state, sessions: {} }, date, defaultCycleAnchor())?.day;
  const next = cycleDay && cycleDay.id === day.id
    ? rest
    : { ...rest, planDayId: day.id, planDayName: day.name, source: 'app', override: true };
  return same(prev, next) ? state : withSession(state, date, next);
}

/* Stamp every trained date that has no stamp yet with the day the current
 * cycle gives it, so a cycle change after this never relabels it. */
export function backfillStamps(state) {
  let next = state;
  for (const date of Object.keys(isObj(state.logs) ? state.logs : {})) {
    const stamp = own(state.sessions, date);
    if (isObj(stamp) && (stamp.planDayId || stamp.planDayName)) continue;
    if (!isTrainingDate(state, date)) continue;
    const p = planDayFor(state, date, defaultCycleAnchor());
    if (p && isObj(p.day)) next = stampSession(next, date, { day: p.day });
  }
  return next;
}

/* The one way to move the cycle: freeze the past, then set the anchor. */
export function setCycleAnchor(state, anchor) {
  const next = backfillStamps(state);
  return next.cycleAnchor === anchor && next === state ? state : { ...next, cycleAnchor: anchor };
}

/* The anchor that makes `date` land on plan index `index`. */
export function anchorFor(date, index) {
  const d = parseAnchor(date);
  d.setDate(d.getDate() - index);
  return dateKey(d);
}

/* Postpone: today's planned day moves to tomorrow (the anchor one day later). */
export function postponedAnchor(state) {
  const d = parseAnchor(state.cycleAnchor) || parseAnchor(defaultCycleAnchor());
  d.setDate(d.getDate() + 1);
  return dateKey(d);
}

/* The next training day after `index` in cycle order, or null. */
export function nextTrainingDay(plan, index) {
  for (let i = 1; i <= plan.length; i += 1) {
    const d = plan[(index + i) % plan.length];
    if (d && !d.isRest) return d;
  }
  return null;
}

/* recovery[date] with only the given fields changed; null or [] removes a
 * field, and an entry without values is removed. */
export function setCheckin(state, date, patch) {
  const prev = isObj(own(state.recovery, date)) ? state.recovery[date] : {};
  const next = { ...prev };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || (Array.isArray(v) && !v.length)) delete next[k];
    else next[k] = v;
  }
  delete next.by;
  const recovery = { ...(isObj(state.recovery) ? state.recovery : {}) };
  if (Object.keys(next).length) recovery[date] = { ...next, by: 'app' };
  else delete recovery[date];
  return same(prev, recovery[date] || {}) ? state : { ...state, recovery };
}

export function checkinHint(c) {
  if (!isObj(c)) return null;
  return (typeof c.sleepH === 'number' && c.sleepH <= 5) || (typeof c.energy === 'number' && c.energy <= 2)
    ? "Short sleep or low energy: consider keeping last session's weights today."
    : null;
}

/* sessions[date] fields (rpe, note, summarySeen); null removes one. */
export function setSessionFields(state, date, patch) {
  const prev = isObj(own(state.sessions, date)) ? state.sessions[date] : {};
  const next = { ...prev };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === '') delete next[k];
    else next[k] = v;
  }
  return same(prev, next) ? state : withSession(state, date, next);
}

/* Session length in whole minutes, only for a plausible live session. */
export function sessionMinutes(session) {
  if (!isObj(session) || typeof session.startedAt !== 'number' || typeof session.lastSetAt !== 'number') return null;
  const min = Math.round((session.lastSetAt - session.startedAt) / 60000);
  return min >= 1 && min <= MAX_SESSION_MIN ? min : null;
}

/* Planned sets done, capped per exercise, and the work beyond the plan. rows:
 * [{ name, planned, target, done }]. */
export function plannedProgress(rows) {
  let total = 0, doneSets = 0;
  const extras = [];
  for (const r of rows) {
    if (r.planned) {
      total += r.target;
      doneSets += Math.min(r.done, r.target);
      if (r.done > r.target) extras.push({ name: r.name, sets: r.done - r.target });
    } else if (r.done > 0) {
      extras.push({ name: r.name, sets: r.done });
    }
  }
  return { total, done: doneSets, pct: total ? Math.round((doneSets / total) * 100) : 0, extras };
}

/* The last earlier date trained as the same plan day, with its volume. */
export function lastSameDay(state, date, planDayId) {
  const dates = Object.keys(isObj(state.logs) ? state.logs : {}).filter(k => k < date).sort().reverse();
  for (const k of dates) {
    if (!isTrainingDate(state, k)) continue;
    if (dayFor(state, k)?.day?.id === planDayId) return { date: k, volume: sumDayVolume(state.logs[k]) };
  }
  return null;
}

/* PR events on a date (logic/records, the same rules as the Stats PR wall). */
export function prsOn(state, date) {
  return buildRecords(state.logs || {}, { exerciseMeta: state.exerciseMeta }).events.filter(e => e.date === date);
}

const shift = (date, n) => { const d = parseAnchor(date); d.setDate(d.getDate() + n); return dateKey(d); };

/* Start a deload on `date` for `days` days (state.deloads, keyed by id). */
export function startDeload(state, date, { days = 7, style = 'volume' } = {}, id) {
  const list = Array.isArray(state.deloads) ? state.deloads : [];
  const entry = { id, from: date, to: shift(date, Math.max(1, days) - 1), style, by: 'app' };
  return { ...state, deloads: [...list, entry] };
}

/* End the deload running on `date`: it stops yesterday, or disappears when it
 * started today. */
export function endDeload(state, date) {
  const cur = deloadOn(state, date);
  if (!cur) return state;
  const list = (Array.isArray(state.deloads) ? state.deloads : []).flatMap(e => {
    if (!e || e.id !== cur.id || e.from !== cur.from) return [e];
    return e.from >= date ? [] : [{ ...e, to: shift(date, -1) }];
  });
  return { ...state, deloads: list };
}
